import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Stack,
  Modal,
} from "@mui/material";
import { API, graphqlOperation } from "aws-amplify";
import { createPart, updateInvoice } from "../../graphql/mutations";
import SearchPricelist from "../search/SearchPricelist";
import { useTheme } from "@mui/material/styles";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "800px",
  maxHeight: "90vh",
  overflow: "auto",
  bgcolor: "#000",
  border: "2px solid #24e4ff",
  borderRadius: "10px",
  boxShadow: 24,
  p: 4,
};

// Search Modal Component
export const SearchModal = ({
  open,
  onClose,
  pricelistName,
  currentInvoice,
  userRecord,
  customerData, // Customer's user record with their provider/group offsets
  fetchInvoiceDetails,
}) => {
  // Use customer's user record for pricing if provided, otherwise use logged-in user
  const pricingUserRecord = customerData || userRecord;

  const handlePartSelect = async (part, grade) => {
    try {
      // Resolve the price from the part with all adjustments already applied
      const resolvedPrice = parseFloat(
        (part &&
          (part.groupAdjustedPrice ??
            part.adjustedPrice ??
            part["PRICE"] ??
            part.price ??
            part.minPrice)) ||
          0,
      );
      const finalPrice = isNaN(resolvedPrice)
        ? 0
        : Math.round(resolvedPrice * (parseFloat(grade) === 0.5 ? 0.5 : 1));
      const partInput = {
        partNumber: part["PART NUMBER"],
        price: finalPrice,
        grade: grade,
        addedByUser: userRecord.id,
        fromPricelist: userRecord.activePricelistID,
        status: "active",
        invoicePartsId: currentInvoice.id,
      };

      await API.graphql(graphqlOperation(createPart, { input: partInput }));

      // Update invoice part count
      await API.graphql(
        graphqlOperation(updateInvoice, {
          input: {
            id: currentInvoice.id,
            partCount: (currentInvoice.partCount || 0) + 1,
          },
        }),
      );

      // Refresh invoice details
      await fetchInvoiceDetails(currentInvoice.id);

      onClose();
    } catch (error) {
      console.error("Error adding part to invoice:", error);
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="search-modal">
      <Box sx={modalStyle}>
        <Typography
          variant="h6"
          sx={{
            color: "#24e4ff",
            marginBottom: "20px",
            textAlign: "center",
            fontFamily: "Poppins",
          }}
        >
          Current Price List: {pricelistName || "None"}
        </Typography>
        <SearchPricelist
          onPartSelect={handlePartSelect}
          showPricelistSelect={false}
          showDisclaimers={false}
          userRecordOverride={pricingUserRecord} // Pass customer's user record for pricing
        />
      </Box>
    </Modal>
  );
};

// Custom Part Modal Component
export const CustomPartModal = ({
  open,
  onClose,
  currentInvoice,
  userRecord,
  fetchInvoiceDetails,
}) => {
  const [customPartData, setCustomPartData] = useState({ name: "", price: "" });

  const handleCustomPartSubmit = async () => {
    try {
      const customInput = {
        partNumber: customPartData.name,
        price: parseFloat(customPartData.price),
        addedByUser: userRecord.id,
        fromPricelist: userRecord.activePricelistID,
        status: "active",
        grade: 1,
        invoicePartsId: currentInvoice.id,
      };

      await API.graphql(graphqlOperation(createPart, { input: customInput }));

      await API.graphql(
        graphqlOperation(updateInvoice, {
          input: {
            id: currentInvoice.id,
            partCount: (currentInvoice.partCount || 0) + 1,
          },
        }),
      );

      await fetchInvoiceDetails(currentInvoice.id);

      setCustomPartData({ name: "", price: "" });
      onClose();
    } catch (error) {
      console.error("Error adding custom part:", error);
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="custom-part-modal">
      <Box sx={modalStyle}>
        <Typography
          variant="h4"
          sx={{ color: "#24e4ff", fontWeight: "bold", marginBottom: "20px" }}
        >
          Add Custom Part
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Part Name"
            name="name"
            value={customPartData.name}
            onChange={(e) =>
              setCustomPartData({ ...customPartData, name: e.target.value })
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "30px",
                backgroundColor: "#000",
                "& fieldset": {
                  borderColor: "#24e4ff",
                  borderWidth: "2px",
                },
                "&:hover fieldset": {
                  borderColor: "#24e4ff",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#24e4ff",
                },
              },
              "& .MuiInputBase-input": {
                color: "#24e4ff",
                padding: "15px 20px",
                fontSize: "18px",
              },
            }}
          />
          <TextField
            label="Price"
            type="number"
            name="price"
            value={customPartData.price}
            onChange={(e) =>
              setCustomPartData({ ...customPartData, price: e.target.value })
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "30px",
                backgroundColor: "#000",
                "& fieldset": {
                  borderColor: "#24e4ff",
                  borderWidth: "2px",
                },
                "&:hover fieldset": {
                  borderColor: "#24e4ff",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#24e4ff",
                },
              },
              "& .MuiInputBase-input": {
                color: "#24e4ff",
                padding: "15px 20px",
                fontSize: "18px",
              },
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              onClick={onClose}
              sx={{
                backgroundColor: "#24e4ff",
                color: "#000",
                borderRadius: "30px",
                padding: "15px 30px",
                fontSize: "16px",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#35d33d",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCustomPartSubmit}
              sx={{
                backgroundColor: "#24e4ff",
                color: "#000",
                borderRadius: "30px",
                padding: "15px 30px",
                fontSize: "16px",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#35d33d",
                },
              }}
            >
              Submit
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};

// Unit By Weight Modal Component
export const UnitByWeightModal = ({
  open,
  onClose,
  currentInvoice,
  userRecord,
  fetchInvoiceDetails,
}) => {
  const [unitByWeightData, setUnitByWeightData] = useState({
    material: "",
    weight: "",
    pricePerUnit: "",
  });
  const theme = useTheme();

  const handleUnitByWeightSubmit = async () => {
    try {
      const weight = parseFloat(unitByWeightData.weight);
      const pricePerUnit = parseFloat(unitByWeightData.pricePerUnit);
      const totalPrice = weight * pricePerUnit;

      const unitInput = {
        partNumber: unitByWeightData.material,
        price: Math.round(totalPrice),
        addedByUser: userRecord.id,
        fromPricelist: userRecord.activePricelistID,
        status: "unit_by_weight",
        grade: 0,
        cost: weight,
        invoicePartsId: currentInvoice.id,
      };

      await API.graphql(graphqlOperation(createPart, { input: unitInput }));

      await API.graphql(
        graphqlOperation(updateInvoice, {
          input: {
            id: currentInvoice.id,
            partCount: (currentInvoice.partCount || 0) + 1,
          },
        }),
      );

      await fetchInvoiceDetails(currentInvoice.id);

      setUnitByWeightData({ material: "", weight: "", pricePerUnit: "" });
      onClose();
    } catch (error) {
      console.error("Error adding unit by weight part:", error);
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="unit-by-weight-modal">
      <Box sx={modalStyle}>
        <Typography
          variant="h4"
          sx={{ color: "#24e4ff", fontWeight: "bold", marginBottom: "20px" }}
        >
          Add Unit By Weight Part
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Material Name"
            name="material"
            value={unitByWeightData.material}
            onChange={(e) =>
              setUnitByWeightData({
                ...unitByWeightData,
                material: e.target.value,
              })
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "30px",
                backgroundColor: "#000",
                "& fieldset": {
                  borderColor: "#24e4ff",
                  borderWidth: "2px",
                },
                "&:hover fieldset": {
                  borderColor: "#24e4ff",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#24e4ff",
                },
              },
              "& .MuiInputBase-input": {
                color: "#24e4ff",
                padding: "15px 20px",
                fontSize: "18px",
              },
            }}
          />
          <TextField
            label="Weight"
            type="number"
            name="weight"
            value={unitByWeightData.weight}
            onChange={(e) =>
              setUnitByWeightData({
                ...unitByWeightData,
                weight: e.target.value,
              })
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "30px",
                backgroundColor: theme.palette.background.paper,
                "& fieldset": {
                  borderColor: theme.palette.primary.main,
                  borderWidth: "2px",
                },
                "&:hover fieldset": {
                  borderColor: theme.palette.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                },
              },
              "& .MuiInputBase-input": {
                color: theme.palette.text.primary,
                padding: "15px 20px",
                fontSize: "18px",
              },
            }}
          />
          <TextField
            label="Price Per Unit"
            type="number"
            name="pricePerUnit"
            value={unitByWeightData.pricePerUnit}
            onChange={(e) =>
              setUnitByWeightData({
                ...unitByWeightData,
                pricePerUnit: e.target.value,
              })
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "30px",
                backgroundColor: theme.palette.background.paper,
                "& fieldset": {
                  borderColor: theme.palette.primary.main,
                  borderWidth: "2px",
                },
                "&:hover fieldset": {
                  borderColor: theme.palette.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                },
              },
              "& .MuiInputBase-input": {
                color: theme.palette.text.primary,
                padding: "15px 20px",
                fontSize: "18px",
              },
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              onClick={onClose}
              sx={{
                backgroundColor: "#24e4ff",
                color: "#000",
                borderRadius: "30px",
                padding: "15px 30px",
                fontSize: "16px",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#35d33d",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUnitByWeightSubmit}
              sx={{
                backgroundColor: "#24e4ff",
                color: "#000",
                borderRadius: "30px",
                padding: "15px 30px",
                fontSize: "16px",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#35d33d",
                },
              }}
            >
              Submit
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};
